import React, { useEffect, useState } from "react";

// Styles
import Styles from "./sidebar.module.scss";

// Constants
import {
  DASHBOARD,
  REPORTS,
  MANAGEAPPS,
  PRODUCTCATALOG,
  BILLING,
  BATCHSERVICE,
  TRIALCENTER,
} from "../../constants/sidebar";
import DashboardSVGNew from "../svg/dashboardSVGnew";
import ProductsSVGNew from "../svg/productsSVGnew";
import AppsSVGNew from "../svg/appsSVGnew";
import DarkLogoSVG from "../svg/darkLogoSVG";
import BatchServiceSVGNew from "../svg/batchServiceSVGnew";
import TrialCenterSVGNew from "../svg/trialCenterSVGnew";
import ReportsSVGNew from "../svg/reportsSVGnew";
import BillingSVG from "../svg/billingSVG";
import ArrowLeftSVG from "../svg/arrowLeft";
import ArrowRightSVG from "../svg/arrowRight";
import DarkLogoSVGMini from "../svg/darkLogoSVGMini";
import {
  ONBOARDING_STATUS,
  normalizeOnboardingStatus,
} from "../../constants/onboardingStatuses";

// Hooks
import { useNavigate, NavLink, useLocation } from "react-router-dom";
import { customEvent } from "../../utils/ga4";
import { cn } from "../utils";
import { testIdProps } from "../../utils/testIdProps";

function Sidebar({
  toggleCollapseSideBar,
  collapsedView,
  toggleSideBar = () => {},
  onboardingStatus,
  batchActivated,
}: any) {
  const location = useLocation();
  const history = useNavigate();
  const [activeLink, setActiveLink] = useState(DASHBOARD);

  // To change the routing onClick of sidenav tabs
  function changeRouting(constant: string, route: string) {
    setActiveLink(constant);
    history(route);
    toggleSideBar(false);
    customEvent("Trial-Center-Page", "Trial-Center-clicked", "Trial-Center");
  }

  // To activate the Tab by route name
  useEffect(() => {
    const url = location.pathname.split("/");
    if (location.pathname === "/" || url.includes("dashboard")) {
      setActiveLink(DASHBOARD);
    } else if (url.includes("reports")) {
      setActiveLink(REPORTS);
    } else if (url.includes("product-catalog")) {
      setActiveLink(PRODUCTCATALOG);
    } else if (url.includes("manage-apps")) {
      setActiveLink(MANAGEAPPS);
    } else if (url.includes("billing")) {
      setActiveLink(BILLING);
    } else if (url.includes("batch-service")) {
      setActiveLink(BATCHSERVICE);
    } else if (url.includes("trial-center")) {
      setActiveLink(TRIALCENTER);
    }
  }, [location.pathname]);

  const menuItems = [
    {
      label: "Home",
      route: "/",
      icon: DashboardSVGNew,
      constant: DASHBOARD,
    },
    {
      label: "Reports",
      route: "/application/reports",
      icon: ReportsSVGNew,
      constant: REPORTS,
    },
    {
      label: "Apps",
      route: "/application/manage-apps",
      icon: AppsSVGNew,
      constant: MANAGEAPPS,
    },

    {
      label: "Products",
      route: "/application/product-catalog",
      icon: ProductsSVGNew,
      constant: PRODUCTCATALOG,
    },
    {
      label: "Billing",
      route: "/application/billing?env=production",
      icon: BillingSVG,
      constant: BILLING,
    },
    // onboardingStatus === ONBOARDING_STATUS.PLAN_SUBSCRIBED && {
    //   label: "NewBilling",
    //   route: "/application/new/billing",
    //   icon: BillingSVG,
    //   constant: BILLING,
    // },
    batchActivated &&
      normalizeOnboardingStatus(onboardingStatus) ===
        ONBOARDING_STATUS.PLAN_SUBSCRIBED && {
        label: "Batch Service",
        route: "/application/batch-service",
        icon: BatchServiceSVGNew,
        constant: BATCHSERVICE,
      },
    {
      label: "Trial Center",
      route: "/application/trial-center",
      icon: TrialCenterSVGNew,
      constant: TRIALCENTER,
    },
  ].filter(Boolean);

  return (
    <div
      className={cn(
        collapsedView ? Styles.sidebarCollapsed : Styles.sideBarBackground
      )}
      style={{ zIndex: 2 }}
    >
      <button
        {...testIdProps("sidebar-collapsed-view")}
        type="button"
        aria-label={collapsedView ? "Expand sidebar" : "Collapse sidebar"}
        onClick={toggleCollapseSideBar}
        className={Styles.collapseToggle}
      >
        {collapsedView ? <ArrowRightSVG /> : <ArrowLeftSVG />}
      </button>

      <div
        className={cn(
          Styles.logoRow,
          collapsedView ? Styles.logoRowCollapsed : Styles.logoRowExpanded
        )}
      >
        {collapsedView ? <DarkLogoSVGMini /> : <DarkLogoSVG />}
      </div>

      <div
        className={cn(
          Styles.menuList,
          collapsedView ? Styles.menuListCollapsed : Styles.menuListExpanded
        )}
      >
        {menuItems.map(({ label, route, icon: Icon, constant }) => (
          <SidebarItem
            key={route}
            route={route}
            label={label}
            constant={constant}
            Icon={Icon}
            isActive={activeLink === constant}
            collapsedView={collapsedView}
            changeRouting={changeRouting}
          />
        ))}
      </div>
    </div>
  );
}

type SidebarItemProps = {
  route: string;
  label: string;
  constant: string;
  Icon: React.ComponentType<{
    isActive: boolean;
    activeColor: string;
    inactiveColor: string;
  }>;
  isActive: boolean;
  collapsedView: boolean;
  changeRouting: (constant: string, route: string) => void;
};

const SidebarItem: React.FC<SidebarItemProps> = ({
  route,
  label,
  constant,
  Icon,
  isActive,
  collapsedView,
  changeRouting,
}) => {
  const [isHovered, setIsHovered] = useState(false);

  return (
    <NavLink
      to={route}
      {...testIdProps(`sidebar-change-routing-${constant.toLowerCase()}`)}
      onClick={() => changeRouting(constant, route)}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      className={cn(
        `nav-link d-flex align-items-center rounded px-1 py-2 mb-2`,
        isActive ? "text-sm fw-medium" : "fw-normal",
        collapsedView && Styles.collapsedHover,
        collapsedView ? "justify-content-center" : "ps-2"
      )}
      style={{
        transition: "background-color 0.2s, border-color 0.2s",
        border: isActive ? "1px solid #21FF90" : "none",
        backgroundColor: isActive
          ? "#F6FEF9"
          : isHovered
            ? "#F6F6F6"
            : "transparent",
        color: isActive ? "#232323" : "#515151",
        position: "relative",
      }}
    >
      <span
        className={cn(
          "d-flex align-items-center justify-content-center rounded p-1 ",
          collapsedView ? "" : "me-2"
        )}
        style={{ width: "32px", height: "32px" }}
      >
        <Icon
          isActive={isActive}
          activeColor="#232323"
          inactiveColor="#868686"
        />
      </span>
      {!collapsedView && <span className="">{label}</span>}

      {collapsedView && (
        <div className={Styles.showTooltip}>
          <span className={Styles.tooltipText}>{label}</span>
        </div>
      )}
    </NavLink>
  );
};

export default Sidebar;
